// Format catalog for the Generate Ads wizard.
//
// The backend's GET /api/ads/formats is the source of truth for which
// platform surfaces are live vs coming_soon (and which ad kinds each
// accepts). The wizard used to hardcode that table, which is exactly how
// Performance Max stayed selectable after the backend froze it to
// coming_soon and started returning 400 PLATFORM_FORMAT_COMING_SOON.
//
// Fallback: if the endpoint is unreachable or returns an empty list we
// still render the known Meta surfaces so the wizard stays usable, but
// we force pmax_16_9 to coming_soon so we never silently offer a format
// we could not verify is live.

import { useEffect, useMemo, useState } from 'react';
import { apiJson } from '../../auth/apiFetch';

export type FormatEntry = {
  key: string;
  label: string;
  aspectRatio: string;
  deliveryDims: { width: number; height: number };
  kinds: string[];
  status: string; // 'live' | 'coming_soon'
  /**
   * Owning platform ('meta' | 'google'), stamped from the enclosing
   * platform block by collectFormats — the per-format API payload does not
   * carry it. Needed because the VIDEO cost rule is per-platform: Meta bills
   * ONE 9:16 master and crops every other aspect from it, while Google PMax
   * bills TWO (9:16 + 16:9, since a landscape cannot be cropped out of a
   * vertical). Without this the picker cannot quote a truthful billable count
   * for a mixed selection. Optional so a stale cached/fallback entry still
   * type-checks; treat absent as unknown, not as Meta.
   */
  platform?: string;
  /**
   * Bands the placement's own native UI covers, as FRACTIONS of frame height
   * (0..1) — the same clamp the backend renderer applies to burned-in titling.
   * Feeds the ad-preview chrome's clear-zone guardrail so it is drawn from the
   * REAL clamp instead of frontend-side approximations; a guardrail that
   * disagrees with the renderer is worse than none, because it is trusted.
   *
   * FRACTIONS, not pixels, and that is load-bearing: the backend's raw
   * `safeArea` is in CANVAS space (width-normalized to 1000), not delivery
   * space, so dividing it by `deliveryDims.height` understates the band —
   * pmax_video_16_9's bottom is 20% of its canvas but reads as 10.5% against
   * delivery. The backend normalises before publishing for exactly that reason.
   *
   * Optional: a backend older than the field omits it, and the guardrail falls
   * back to its own per-placement defaults rather than drawing nothing.
   */
  safeAreaPct?: { top: number; bottom: number };
};

export type FormatPreset = {
  key: string;
  label: string;
  description: string;
  formats: FormatEntry[];
};

export type FormatPlatform = {
  id: string;
  label: string;
  presets: FormatPreset[];
  formats: FormatEntry[];
};

/**
 * A whole-run workflow other than "tick the surfaces you want" (API contract
 * §1). Today the only one is the scene reel: a 10 s video cut from four
 * generated scenes, delivered at 9:16 + 16:9 (generated) and 1:1 (cropped).
 * The backend omits `workflows` (or sends []) while the workflow is switched
 * off, and the wizard then offers nothing new — see sceneReelWorkflowFrom.
 */
export type WorkflowModel = {
  key: string;
  label: string;
  default: boolean;
  available: boolean;
};

export type WorkflowEntry = {
  key: string;
  enabled: boolean;
  label: string;
  description: string;
  durationSec: number;
  cut: number[];
  outputs: Array<{ aspect: string; platformFormat: string; derived: boolean }>;
  maxProducts: number;
  maxSeedPicksPerProduct: number;
  models: WorkflowModel[];
  estUsdPerProduct: { low: number; high: number } | null;
  ceilingUsdPerProduct: number | null;
};

export type FormatCatalogResponse = { platforms: FormatPlatform[]; workflows?: unknown };

export type FormatCatalogState = {
  formats: FormatEntry[];
  /** True when we could not load / verify the live catalog. */
  fromFallback: boolean;
  loading: boolean;
  byKey: Map<string, FormatEntry>;
  /**
   * The enabled scene-reel workflow, or null — absent from the payload, an
   * older backend, the built-in fallback, or a malformed entry. Null means
   * the wizard looks exactly as it did before the workflow existed.
   */
  sceneReelWorkflow: WorkflowEntry | null;
};

export const SCENE_REEL_WORKFLOW_KEY = 'scene_reel';

function positiveNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Parse the scene-reel entry out of GET /api/ads/formats `workflows`.
 * Fail closed: anything short of an enabled, well-formed entry with at least
 * one available model returns null and the option stays hidden.
 */
export function sceneReelWorkflowFrom(workflows: unknown): WorkflowEntry | null {
  if (!Array.isArray(workflows)) return null;
  const raw = workflows.find(w => w && typeof w === 'object' && (w as { key?: unknown }).key === SCENE_REEL_WORKFLOW_KEY);
  if (!raw || (raw as { enabled?: unknown }).enabled !== true) return null;
  const w = raw as Record<string, unknown>;
  const models: WorkflowModel[] = Array.isArray(w.models)
    ? w.models
        .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object' && typeof (m as { key?: unknown }).key === 'string')
        .map(m => ({
          key:       String(m.key),
          label:     typeof m.label === 'string' && m.label ? m.label : String(m.key),
          default:   m.default === true,
          available: m.available === true
        }))
    : [];
  if (!models.some(m => m.available)) return null;
  const maxProducts = positiveNumber(w.maxProducts);
  if (maxProducts == null) return null;
  const outputs = Array.isArray(w.outputs)
    ? w.outputs
        .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object' && typeof (o as { aspect?: unknown }).aspect === 'string')
        .map(o => ({
          aspect:         String(o.aspect),
          platformFormat: typeof o.platformFormat === 'string' ? o.platformFormat : '',
          derived:        o.derived === true
        }))
    : [];
  const cut = Array.isArray(w.cut) && w.cut.length && w.cut.every(n => positiveNumber(n) != null)
    ? (w.cut as number[]).slice()
    : [3, 3, 2, 2];
  const est = w.estUsdPerProduct && typeof w.estUsdPerProduct === 'object'
    ? w.estUsdPerProduct as Record<string, unknown>
    : null;
  const estLow = est ? positiveNumber(est.low) : null;
  const estHigh = est ? positiveNumber(est.high) : null;
  return {
    key:                    SCENE_REEL_WORKFLOW_KEY,
    enabled:                true,
    label:                  typeof w.label === 'string' && w.label ? w.label : 'Scene reel',
    description:            typeof w.description === 'string' ? w.description : '',
    durationSec:            positiveNumber(w.durationSec) ?? cut.reduce((a, b) => a + b, 0),
    cut,
    outputs,
    maxProducts:            Math.floor(maxProducts),
    maxSeedPicksPerProduct: Math.floor(positiveNumber(w.maxSeedPicksPerProduct) ?? 3),
    models,
    estUsdPerProduct:       estLow != null && estHigh != null ? { low: estLow, high: estHigh } : null,
    ceilingUsdPerProduct:   positiveNumber(w.ceilingUsdPerProduct)
  };
}

/** The model a fresh scene-reel selection starts on: the default if available, else the first available. */
export function defaultSceneModelKey(workflow: WorkflowEntry | null): string | null {
  if (!workflow) return null;
  const available = workflow.models.filter(m => m.available);
  return (available.find(m => m.default) || available[0])?.key ?? null;
}

// Conservative offline table. Meta surfaces stay selectable so a catalog
// outage doesn't brick the wizard; pmax is coming_soon until the API
// explicitly says otherwise.
const FALLBACK_FORMATS: FormatEntry[] = [
  {
    key: 'meta_feed_1_1',
    label: 'Meta Feed',
    aspectRatio: '1:1',
    deliveryDims: { width: 1080, height: 1080 },
    kinds: ['image', 'video'],
    platform: 'meta',
    status: 'live'
  },
  {
    key: 'meta_feed_4_5',
    label: 'Meta Feed',
    aspectRatio: '4:5',
    deliveryDims: { width: 1080, height: 1350 },
    kinds: ['image', 'video'],
    platform: 'meta',
    status: 'live'
  },
  {
    key: 'meta_reels_9_16',
    label: 'Meta Reels',
    aspectRatio: '9:16',
    deliveryDims: { width: 1080, height: 1920 },
    kinds: ['video'],
    platform: 'meta',
    status: 'live'
  },
  {
    key: 'meta_stories_9_16',
    label: 'Meta Stories',
    aspectRatio: '9:16',
    deliveryDims: { width: 1080, height: 1920 },
    kinds: ['image', 'video'],
    platform: 'meta',
    status: 'live'
  },
  {
    key: 'pmax_16_9',
    label: 'Performance Max',
    aspectRatio: '16:9',
    deliveryDims: { width: 1920, height: 1080 },
    kinds: ['image', 'video'],
    platform: 'google',
    status: 'coming_soon'
  }
];

// Module-level in-flight so PlatformFormatPicker + Step2 share one request
// per page load instead of each firing their own.
type LoadedCatalog = { formats: FormatEntry[]; fromFallback: boolean; sceneReelWorkflow: WorkflowEntry | null };
let inflight: Promise<LoadedCatalog> | null = null;

function collectFormats(platforms: FormatPlatform[]): FormatEntry[] {
  // Prefer platform.formats; fold preset formats only when missing so we
  // don't double-list the same key if the backend includes both.
  const byKey = new Map<string, FormatEntry>();
  // Stamp the owning platform as we descend — it is a property of the block the
  // format sits in, not of the format payload, so it is lost the moment these
  // are flattened into one list. See FormatEntry.platform for why it matters.
  for (const p of platforms) {
    for (const f of p.formats || []) {
      if (f?.key) byKey.set(f.key, { ...f, platform: f.platform ?? p.id });
    }
    for (const preset of p.presets || []) {
      for (const f of preset.formats || []) {
        if (f?.key && !byKey.has(f.key)) byKey.set(f.key, { ...f, platform: f.platform ?? p.id });
      }
    }
  }
  return Array.from(byKey.values());
}

function loadFormatCatalog(): Promise<LoadedCatalog> {
  if (!inflight) {
    inflight = apiJson<FormatCatalogResponse>('/api/ads/formats')
      .then(res => {
        const collected = collectFormats(res.platforms || []);
        if (!collected.length) {
          // Empty body is as unusable as a network error — fall back
          // rather than rendering zero cards.
          return { formats: FALLBACK_FORMATS, fromFallback: true, sceneReelWorkflow: null };
        }
        return { formats: collected, fromFallback: false, sceneReelWorkflow: sceneReelWorkflowFrom(res.workflows) };
      })
      // The fallback never offers a workflow: it could not be verified live.
      .catch(() => ({ formats: FALLBACK_FORMATS, fromFallback: true, sceneReelWorkflow: null }));
  }
  return inflight;
}

export function isFormatLive(entry: FormatEntry | undefined): boolean {
  return !!entry && entry.status === 'live';
}

/** image/video kinds a format accepts (drops unknown strings). */
export function imageVideoKinds(entry: FormatEntry | undefined): Array<'image' | 'video'> {
  const raw = entry?.kinds ?? ['image', 'video'];
  return raw.filter((k): k is 'image' | 'video' => k === 'image' || k === 'video');
}

/** First live format, preferring the historical Meta Feed 1:1 default. */
export function firstLiveFormatKey(formats: FormatEntry[]): string | null {
  const preferred = formats.find(f => f.key === 'meta_feed_1_1' && isFormatLive(f));
  if (preferred) return preferred.key;
  const any = formats.find(isFormatLive);
  return any?.key ?? null;
}

/**
 * Subtitle under the card title. Keeps the old hard-coded wording for
 * known formats so this stays a data-source change, not a redesign.
 */
export function formatSubtitle(entry: FormatEntry): string {
  if (entry.key === 'meta_reels_9_16') {
    return 'Vertical Video (safe area top + bottom)';
  }
  const aspectWord =
    entry.aspectRatio === '1:1'  ? 'Square' :
    entry.aspectRatio === '4:5'  ? 'Portrait' :
    entry.aspectRatio === '9:16' ? 'Vertical' :
    entry.aspectRatio === '16:9' ? 'Landscape' :
    entry.aspectRatio;
  const kinds = imageVideoKinds(entry);
  const hasImage = kinds.includes('image');
  const hasVideo = kinds.includes('video');
  const kindWord =
    hasImage && hasVideo ? 'Image or Video' :
    hasVideo             ? 'Video' :
    hasImage             ? 'Image' :
    '';
  return kindWord ? `${aspectWord} — ${kindWord}` : aspectWord;
}

/** Kind toggles shown on a card (adds 'both' when image+video are allowed). */
export function uiAllowedKinds(entry: FormatEntry): Array<'image' | 'video' | 'both'> {
  const kinds = imageVideoKinds(entry);
  if (kinds.includes('image') && kinds.includes('video')) {
    return ['image', 'video', 'both'];
  }
  return kinds;
}

export function lockedKindFor(entry: FormatEntry): 'image' | 'video' | undefined {
  const kinds = imageVideoKinds(entry);
  if (kinds.length === 1) return kinds[0];
  return undefined;
}

export function useFormatCatalog(): FormatCatalogState {
  // Seed with the conservative fallback so the picker never renders empty
  // while the request is in flight.
  const [formats, setFormats] = useState<FormatEntry[]>(FALLBACK_FORMATS);
  const [fromFallback, setFromFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sceneReelWorkflow, setSceneReelWorkflow] = useState<WorkflowEntry | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadFormatCatalog().then(result => {
      if (cancelled) return;
      setFormats(result.formats);
      setFromFallback(result.fromFallback);
      setSceneReelWorkflow(result.sceneReelWorkflow);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const byKey = useMemo(
    () => new Map(formats.map(f => [f.key, f])),
    [formats]
  );

  return { formats, fromFallback, loading, byKey, sceneReelWorkflow };
}
